import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory, useRouteMatch } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

import DashboardSidebar from '../../../agencyDashboardSidebar';
import ShiftHeader      from './ShiftHeader';
import OfficerRoster    from './OfficerRoster';
import OfferPanel       from './OfferPanel';
import { getShiftDetail, resolveDisputedOffer } from '../../../../../../services/staffingService';

/**
 * ShiftDetail — Shift Command Center
 * ─────────────────────────────────────────────────────────────────────────────
 * PARENT component. Owns ALL state. Children receive only props.
 * No cross-child shared state. No context. One file to understand the whole page.
 *
 * Handles TWO routes (no re-mount between them — useRouteMatch reads the optional suffix):
 *   /dispatch/shift/:shiftId                    → roster only, panel closed
 *   /dispatch/shift/:shiftId/offer/:offerId     → roster + OfferPanel pre-opened
 *
 * Layout:
 *   Panel closed  → max-w-3xl centred  (ShiftHeader above, OfficerRoster below)
 *   Panel open    → max-w-7xl two-col  (roster left 320px, OfferPanel fills right)
 *
 * Clicking an officer row → history.push(.../offer/:offerId)  [no re-mount; just URL change]
 * OfferPanel × button     → history.push(.../shift/:shiftId)  [collapse panel]
 * Dispute resolution runs inline in OfferPanel; parent refreshes offers[] on success.
 *
 * Data notes:
 *   - One shift can have MULTIPLE simultaneous active offers (one per officer)
 *   - offer.shifts[0].checkIn / checkOut hold the embedded per-shift state
 *   - parentOfferId chains revised offers (officer declined → manager resent)
 *   - Disputed offers BLOCK payout until resolved — resolution is inline here
 */
const ShiftDetailMaster = () => {
  const { providerCompanyId, shiftId } = useParams();
  const history = useHistory();

  // Detect optional /offer/:offerId suffix without re-mounting
  const offerRouteMatch = useRouteMatch(
    '/provider-company/:providerCompanyId/dispatch/shift/:shiftId/offer/:offerId'
  );
  const selectedOfferId = offerRouteMatch?.params?.offerId || null;

  // ── State ──────────────────────────────────────────────────────────────────
  const [shiftData,        setShiftData]        = useState(null);
  const [customerName,     setCustomerName]     = useState('');
  const [customerLocalId,  setCustomerLocalId]  = useState(null);
  const [customerContact,  setCustomerContact]  = useState(null);
  const [serviceType,      setServiceType]      = useState('');
  const [hourlyPayRate,    setHourlyPayRate]    = useState(null);
  const [formattedAddress, setFormattedAddress] = useState(null);
  const [location,         setLocation]         = useState(null);
  const [address,          setAddress]          = useState(null);
  const [timezone,         setTimezone]         = useState(null);
  const [memo,             setMemo]             = useState(null);
  const [filledCount,      setFilledCount]      = useState(0);
  const [offers,           setOffers]           = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState(null);
  const [resolving,        setResolving]        = useState(false);
  const [showSidebar,      setShowSidebar]      = useState(false);

  // Derived: the currently selected offer (read from already-fetched offers[])
  const selectedOffer = selectedOfferId
    ? offers.find((o) => o._id?.toString() === selectedOfferId) ?? null
    : null;

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchShift = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getShiftDetail(providerCompanyId, shiftId);
      const {
        shift, customerName: cName, customerLocalId: cLocalId, customerContact: cContact,
        serviceType: sType, hourlyPayRate: hRate, formattedAddress: fAddr,
        location: loc, address: addr, timezone: tz, memo: jobMemo,
        filledCount: fCount, offers: offerList,
      } = resp.data;
      setShiftData(shift);
      setCustomerName(cName || '');
      setCustomerLocalId(cLocalId || null);
      setCustomerContact(cContact || null);
      setServiceType(sType || '');
      setHourlyPayRate(hRate || null);
      setFormattedAddress(fAddr || null);
      setLocation(loc || null);
      setAddress(addr || null);
      setTimezone(tz || null);
      setMemo(jobMemo || null);
      setFilledCount(fCount ?? 0);
      setOffers(offerList || []);
    } catch (err) {
      console.error('[ShiftDetail] fetch error:', err);
      setError('Failed to load shift details. Please try again.');
      toast.error('Failed to load shift details');
    } finally {
      setLoading(false);
    }
  }, [providerCompanyId, shiftId]);

  useEffect(() => { fetchShift(); }, [fetchShift]);

  // Auto-open the first (highest-priority) offer when none is selected.
  // Uses replace so the back button still exits the page cleanly.
  useEffect(() => {
    if (!loading && !selectedOfferId && offers.length > 0) {
      const first = offers[0];
      history.replace(
        `/provider-company/${providerCompanyId}/dispatch/shift/${shiftId}/offer/${first._id}`
      );
    }
  }, [loading, offers, selectedOfferId, history, providerCompanyId, shiftId]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  /** Navigate back to the Schedule tab of Dispatch */
  const handleGoBack = useCallback(() => {
    history.push(`/provider-company/${providerCompanyId}/dispatch?tab=schedule`);
  }, [history, providerCompanyId]);

  /**
   * Navigate to JobDispatchDetail (allocation tool).
   * The ONLY place offers can be sent to officers.
   */
  const handleGoToJob = useCallback((jobId) => {
    history.push(`/provider-company/${providerCompanyId}/dispatch/job/${jobId}`);
  }, [history, providerCompanyId]);

  /**
   * Open OfferPanel for a specific offer.
   * Uses pushes .../offer/:offerId on the same component — no re-mount, no re-fetch.
   */
  const handleSelectOffer = useCallback((offer) => {
    const offerId = offer?._id;
    if (!offerId) return;
    history.push(`/provider-company/${providerCompanyId}/dispatch/shift/${shiftId}/offer/${offerId}`);
  }, [history, providerCompanyId, shiftId]);

  /** Close OfferPanel — exit back to the Schedule tab (no intermediate no-panel state) */
  const handleClosePanel = useCallback(() => {
    history.push(`/provider-company/${providerCompanyId}/dispatch?tab=schedule`);
  }, [history, providerCompanyId]);

  /** Resolve a disputed offer inline — refreshes offers[] without leaving the page */
  const handleResolveDispute = useCallback(async (reason) => {
    if (!selectedOfferId || !reason.trim()) return;
    setResolving(true);
    try {
      await resolveDisputedOffer(providerCompanyId, selectedOfferId, reason);
      toast.success('Dispute resolved');
      await fetchShift(); // refresh offers[] in place
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resolve dispute');
    } finally {
      setResolving(false);
    }
  }, [providerCompanyId, selectedOfferId]); // fetchShift added below via ref pattern

  // ── Render ─────────────────────────────────────────────────────────────────
  // panelOpen is always true when offers exist (auto-select effect ensures this).
  // Two-column layout is shown whenever there are offers; single-column otherwise.
  const hasOffers = offers.length > 0;

  return (
    <div className="flex h-screen overflow-hidden bg-background font-inter">
      <DashboardSidebar isOpen={showSidebar} setIsOpen={setShowSidebar} />

      <main className="flex-1 overflow-y-auto">
        <div className={`mx-auto p-6 space-y-6 transition-all duration-200 ${
          hasOffers ? 'max-w-7xl' : 'max-w-3xl'
        }`}>

          {loading ? (
            // ── Loading skeleton ──
            <div className="space-y-6">
              {/* Header skeleton */}
              <div className="h-6 w-32 animate-pulse rounded bg-muted" />
              <div className="rounded-xl border border-border bg-card p-5 shadow-sm space-y-3">
                <div className="h-5 w-48 animate-pulse rounded bg-muted" />
                <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                <div className="h-4 w-56 animate-pulse rounded bg-muted" />
              </div>
              {/* Roster skeleton */}
              <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                <div className="bg-muted/20 border-b border-border px-4 py-3">
                  <div className="h-3 w-24 animate-pulse rounded bg-muted" />
                </div>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-b-0">
                    <div className="h-8 w-8 animate-pulse rounded-full bg-muted flex-shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 w-32 animate-pulse rounded bg-muted" />
                      <div className="h-2.5 w-20 animate-pulse rounded bg-muted" />
                    </div>
                    <div className="h-5 w-16 animate-pulse rounded-full bg-muted" />
                  </div>
                ))}
              </div>
            </div>
          ) : error ? (
            // ── Error state ──
            <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-border bg-card py-16 text-center shadow-sm">
              <p className="text-sm font-semibold text-foreground">Could not load shift</p>
              <p className="text-xs text-muted-foreground">{error}</p>
              <button
                type="button"
                onClick={fetchShift}
                className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                <Loader2 size={13} />
                Try again
              </button>
            </div>
          ) : (
            // ── Loaded ──
            <>
              <ShiftHeader
                shiftData={shiftData}
                customerName={customerName}
                customerLocalId={customerLocalId}
                customerContact={customerContact}
                serviceType={serviceType}
                hourlyPayRate={hourlyPayRate}
                formattedAddress={formattedAddress}
                location={location}
                address={address}
                timezone={timezone}
                memo={memo}
                filledCount={filledCount}
                providerCompanyId={providerCompanyId}
                onGoBack={handleGoBack}
                onGoToJob={handleGoToJob}
              />

              {/* Two-column when offers exist; single column for empty shift */}
              <div className={hasOffers ? 'flex gap-4 items-start' : undefined}>
                {/* Roster — fixed width in two-column mode */}
                <div className={hasOffers ? 'w-72 flex-shrink-0' : undefined}>
                  <OfficerRoster
                    offers={offers}
                    selectedOfferId={selectedOfferId}
                    onSelectOffer={handleSelectOffer}
                    onGoToJob={handleGoToJob}
                    jobId={shiftData?.jobId}
                  />
                </div>

                {/* Offer panel — always rendered when an offer is selected */}
                {selectedOfferId && (
                  <div className="flex-1 min-w-0">
                    <OfferPanel
                      offer={selectedOffer}
                      onClose={handleClosePanel}
                      onResolve={handleResolveDispute}
                      resolving={resolving}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ShiftDetailMaster;
